import * as React from "react";

/**
 * A browser draft, kept by the kit in this tab's session storage (CASE-038, O7).
 *
 * It survives Hide, Back and a reload in its own tab, and ends with its work:
 * a confirmed commit, a discard, the newer record chosen over it, sign-out or
 * the tab closing. Kept across tabs or closes, a message or a mark would
 * outlive the session on a shared computer; a draft that must outlive the tab
 * is a server draft. No area writes its own storage.
 */

const PREFIX = "imam:bento-draft:v1:";

/** The signed-in login a draft belongs to. Without one, a draft is kept only while its page is drawn. */
export interface TabDraftOwner {
  tenant: string;
  user: string;
}

// Undefined while the session still resolves who is signed in.
const Owner = React.createContext<TabDraftOwner | null | undefined>(null);

/** Set once by the app's session, so every key carries its tenant and login: undefined while it resolves, null when no one is signed in. */
export function TabDraftOwnerProvider({ owner, children }: { owner: TabDraftOwner | null | undefined; children: React.ReactNode }) {
  const tenant = owner?.tenant;
  const user = owner?.user;
  const resolving = owner === undefined;
  const value = React.useMemo(() => (resolving ? undefined : tenant && user ? { tenant, user } : null), [resolving, tenant, user]);
  return React.createElement(Owner.Provider, { value }, children);
}

export interface TabDraftSpec<V> {
  /**
   * The draft's namespace: the box's id, or one canonical id for a controller
   * drawn in several places, such as "review-<work id>" for Review in its own
   * List, lifted into Coursework and in a visit, so one person finds one draft.
   * It needn't be the rendered box id.
   */
  box: string;
  /** The record inside the box the draft edits, as the area names it ("work:42/enrollment:7"). A row task uses its row's key. */
  record: string;
  /** The record's version now, opaque (an updated_at keeps its microseconds); null for a record not yet made. */
  version: string | null;
  /** The record's values now. A draft begins from them, and loadNewer and discard return to them. */
  initial: V;
}

export interface TabDraft<V, P = unknown> {
  /** What the controls draw and the commit sends. */
  value: V;
  /** As React's setState: a value or an updater. Kept in this tab at once. */
  set(next: V | ((was: V) => V)): void;
  /**
   * Who is signed in is known, so `value` and `kept` are final. Until then a
   * page drawn before the session resolves shows its read as loading, so its
   * first ready face already says the draft it keeps.
   */
  settled: boolean;
  /** The draft differs from the record. */
  kept: boolean;
  /** It is kept in this tab's storage, so a reload brings it back; only then does a hidden box or row say "Draft kept in this tab". */
  inTab: boolean;
  /** The record version the draft began from. */
  began: string | null;
  /** A kept draft began from another version than the record's now: the part shows it as its conflict, and nothing is applied until the person chooses (R4). */
  conflict: { began: string | null; now: string | null } | null;
  /** Keep the draft over the newer record: it now begins from the record's version. */
  keepMine(): void;
  /** Take the newer record: the draft ends. */
  loadNewer(): void;
  /** The person discards the draft. */
  discard(): void;
  /**
   * A send begun from this draft, kept with it until its answer, such as the
   * command's key and the version it was sent from. After a reload the area
   * offers Check on that same key and never sends again (W1). The kit doesn't
   * read it.
   */
  pending: P | null;
  setPending(pending: P | null): void;
  /** The draft and its pending send end now, such as on a discard the area offers. */
  end(): void;
  /**
   * The kit calls this as a commit sends the draft, and the returned call when
   * that commit is confirmed. A send keeps what it carried with the draft, so
   * `again`, a Check or a Retry of the send already out (even after a reload),
   * answers for that, never for text typed since. The answer ends the draft
   * only if it is still what was sent. Text added since stays kept and begins
   * from the version the send made, when the confirmation says it; without it,
   * any version read next is a conflict, never adopted silently (W1, R4).
   */
  sending(again?: boolean): (version?: string) => void;
}

interface Stored {
  began: string | null;
  value: unknown;
  pending: unknown;
  /** The send out from this draft: what it carried, under its own id. */
  sent?: { id: string; value: unknown };
}

let sends = 0;
const sendId = () => `${Date.now().toString(36)}-${++sends}`;

const listeners = new Set<() => void>();
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
// Counts every change, so a hook over many records draws again when any of them moves.
let revision = 0;
const changed = () => {
  revision++;
  listeners.forEach((listener) => listener());
};

// Where storage is full or disabled, a draft is kept here, for as long as this page lives.
const unstored = new Map<string, string>();

function readRaw(key: string): string | null {
  if (unstored.has(key)) return unstored.get(key)!;
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeRaw(key: string, stored: Stored | null) {
  unstored.delete(key);
  try {
    if (stored) sessionStorage.setItem(key, JSON.stringify(stored));
    else sessionStorage.removeItem(key);
  } catch {
    if (stored) unstored.set(key, JSON.stringify(stored));
  }
  changed();
}

function parse(raw: string | null): Stored | null {
  if (!raw) return null;
  try {
    const stored = JSON.parse(raw);
    return stored && typeof stored === "object" && "value" in stored && (stored.began === null || typeof stored.began === "string")
      ? {
          began: stored.began,
          value: stored.value,
          pending: stored.pending ?? null,
          ...(stored.sent && typeof stored.sent.id === "string" && "value" in stored.sent ? { sent: { id: stored.sent.id, value: stored.sent.value } } : {}),
        }
      : null;
  } catch {
    return null;
  }
}

/** Sign-out ends every draft this tab keeps, for every login. */
export function clearTabDrafts() {
  unstored.clear();
  try {
    for (let index = sessionStorage.length - 1; index >= 0; index--) {
      const key = sessionStorage.key(index);
      if (key?.startsWith(PREFIX)) sessionStorage.removeItem(key);
    }
  } catch {
    // Storage may be disabled; signing out must still complete.
  }
  changed();
}

// Each part is encoded, so "note:course" + "42" and "note" + "course:42" are two drafts.
const keyOf = (owner: TabDraftOwner, box: string, record: string) => PREFIX + [owner.tenant, owner.user, box, record].map(encodeURIComponent).join(":");
// Without a login a draft is held in the page, under its box and record alike, so another box's record never takes it.
const heldKeyOf = (box: string, record: string) => [box, record].map(encodeURIComponent).join(":");
const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/** One record's draft, read from `load` and written through `put`, as of this render. */
function draftOf<V, P>(load: () => string | null, put: (next: Stored | null) => void, stored: boolean, settled: boolean, version: string | null, initial: V): TabDraft<V, P> {
  const current = () => parse(load());
  const beganOf = (draft: Stored | null) => (draft === null ? version : draft.began);
  const was = current();
  const kept = was !== null && !same(was.value, initial);
  const end = () => put(null);
  return {
    value: (was ? was.value : initial) as V,
    set(next) {
      const was = current();
      const value = typeof next === "function" ? (next as (was: V) => V)((was ? was.value : initial) as V) : next;
      // Back where the record is, there is no draft, unless a send from it is still unanswered.
      if (same(value, initial) && !was?.pending && !was?.sent) return put(null);
      put(was ? { ...was, value } : { began: version, value, pending: null });
    },
    settled,
    kept,
    inTab: kept && stored,
    began: beganOf(was),
    conflict: was && kept && beganOf(was) !== version ? { began: was.began, now: version } : null,
    keepMine() {
      const was = current();
      if (was) put({ ...was, began: version });
    },
    loadNewer: end,
    discard: end,
    pending: (was?.pending ?? null) as P | null,
    setPending(pending) {
      const was = current();
      if (pending === null && (!was || (same(was.value, initial) && !was.sent))) return put(null);
      put(was ? { ...was, pending } : { began: version, value: initial, pending });
    },
    end,
    sending(again = false) {
      const at = current();
      if (!at) return () => undefined;
      const sent = again && at.sent ? at.sent : { id: sendId(), value: at.value };
      if (sent !== at.sent) put({ ...at, sent });
      return (made) => {
        const now = current();
        // Ended already, or a newer send is out, whose own answer decides.
        if (!now || now.sent?.id !== sent.id) return;
        if (same(now.value, sent.value)) return put(null);
        // The version this send made is the newer text's baseline; any other read after it is a conflict (R4).
        put({ began: made ?? now.began, value: now.value, pending: null });
      };
    },
  };
}

export function useTabDraft<V, P = unknown>({ box, record, version, initial }: TabDraftSpec<V>): TabDraft<V, P> {
  const owner = React.useContext(Owner);
  const key = owner ? keyOf(owner, box, record) : null;
  // Without a login a draft is held here only, by record, never under a key another login could read.
  const [held, setHeld] = React.useState<Readonly<Record<string, string>>>({});
  const heldNow = React.useRef(held);
  heldNow.current = held;
  React.useSyncExternalStore(subscribe, () => (key ? readRaw(key) : held[heldKeyOf(box, record)] ?? null), () => null);
  const draft = draftOf<V, P>(
    () => (key ? readRaw(key) : heldNow.current[heldKeyOf(box, record)] ?? null),
    (next) => {
      if (key) return writeRaw(key, next);
      const { [heldKeyOf(box, record)]: _gone, ...rest } = heldNow.current;
      heldNow.current = next ? { ...rest, [heldKeyOf(box, record)]: JSON.stringify(next) } : rest;
      setHeld(heldNow.current);
    },
    key !== null && !unstored.has(key),
    owner !== undefined,
    version,
    initial,
  );
  // The acts keep one identity across renders and act on the record the hook holds when called;
  // `sending` binds the record it sent, so its answer never reaches a later one.
  const latest = React.useRef(draft);
  latest.current = draft;
  const acts = React.useMemo(
    () => ({
      set: (next: V | ((was: V) => V)) => latest.current.set(next),
      keepMine: () => latest.current.keepMine(),
      end: () => latest.current.end(),
      setPending: (pending: P | null) => latest.current.setPending(pending),
      sending: (again?: boolean) => latest.current.sending(again),
    }),
    [],
  );
  return { ...draft, ...acts, loadNewer: acts.end, discard: acts.end };
}

/** A confirmed result outlives its draft until the person starts another task.
 * Share the kit's tenant/login/tab storage and sign-out cleanup, but never
 * attach this receipt to a Part: confirming a draft must not erase its receipt.
 */
export function useTabReceipt<T>(box: string, record: string) {
  const stored = useTabDraft<T | null>({ box: `${box}:receipt`, record, version: null, initial: null });
  return { value: stored.value, settled: stored.settled, keep: stored.set, clear: stored.end };
}

/**
 * Many records' drafts in one box, such as a List whose rows each hold their
 * own task: `of` binds one record, so a row's commit ends only that row's
 * draft. Call it while building each row; its acts are made for each render.
 */
export function useTabDrafts<V, P = unknown>(box: string): (spec: Omit<TabDraftSpec<V>, "box">) => TabDraft<V, P> {
  const owner = React.useContext(Owner);
  const [held, setHeld] = React.useState<Readonly<Record<string, string>>>({});
  const heldNow = React.useRef(held);
  heldNow.current = held;
  React.useSyncExternalStore(subscribe, () => revision, () => 0);
  return ({ record, version, initial }) => {
    const key = owner ? keyOf(owner, box, record) : null;
    return draftOf<V, P>(
      () => (key ? readRaw(key) : heldNow.current[heldKeyOf(box, record)] ?? null),
      (next) => {
        if (key) return writeRaw(key, next);
        const { [heldKeyOf(box, record)]: _gone, ...rest } = heldNow.current;
        heldNow.current = next ? { ...rest, [heldKeyOf(box, record)]: JSON.stringify(next) } : rest;
        setHeld(heldNow.current);
      },
      key !== null && !unstored.has(key),
      owner !== undefined,
      version,
      initial,
    );
  };
}
